import React, { useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  FormHelperText
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import ErrorIcon from '@mui/icons-material/Error'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useGetCampaignsQuery } from 'src/services/campaign-service'
import { useAddWebhookMutation, useSendDummyDataMutation } from 'src/services/integration-service'
import toast from 'react-hot-toast'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

const eventOptions = [
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Email Sent', value: 'email_sent' },
  { icon: <ErrorIcon sx={{ fontSize: '16px' }} />, text: 'Email Bounced', value: 'email_bounced' },
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Email Opened', value: 'email_opened' },
  { icon: <CheckCircleIcon sx={{ fontSize: '16px' }} />, text: 'Reply Received', value: 'reply_received' },
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Email Link Clicked', value: 'email_link_clicked' },
  { icon: <ThumbUpIcon sx={{ fontSize: '16px' }} />, text: 'Lead is marked as interested', value: 'lead_marked_interested' },
  { icon: <ThumbDownIcon sx={{ fontSize: '16px' }} />, text: 'Lead is marked as not interested', value: 'lead_marked_not_interested' },
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Lead is marked as neutral', value: 'lead_marked_neutral' },
  { icon: <CheckCircleIcon sx={{ fontSize: '16px' }} />, text: 'Campaign Completed', value: 'campaign_completed' },
  { icon: <CloseIcon sx={{ fontSize: '16px' }} />, text: 'Lead status changed to "Meeting Booked"', value: 'meeting_booked' },
  { icon: <ThumbUpIcon sx={{ fontSize: '16px' }} />, text: 'Lead status changed to "Meeting Complete | Not Closed"', value: 'meeting_complete_not_closed' },
  { icon: <CloseIcon sx={{ fontSize: '16px' }} />, text: 'Lead status changed to "Closed"', value: 'closed' },
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Lead status changed to "Out of Office"', value: 'out_of_office' },
  { icon: <ErrorIcon sx={{ fontSize: '16px' }} />, text: 'Lead status changed to "Wrong Person"', value: 'wrong_person' },
  { icon: <EmailIcon sx={{ fontSize: '16px' }} />, text: 'Lead Unsubscribed', value: 'lead_unsubscribed' },
  { icon: <ErrorIcon sx={{ fontSize: '16px' }} />, text: 'Email Account Error', value: 'email_account_error' }
]

// Validation schema using Yup
const validationSchema = Yup.object({
  webhookUrl: Yup.string()
    .required('This field is required')
    .matches(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i, 'Invalid URL format'),
  eventType: Yup.string()
    .required('This field is required'),
  campaign: Yup.object().shape({
    id: Yup.string().required('This field is required'),
    name: Yup.string().required('This field is required')
  })
})

const AddWebhookDialog = ({
  openAddWebhookDialog,
  setOpenAddWebhookDialog,
  setIntegration,
  workspaceId,
  eventType,
  setEventType
}) => {
  const [isCampaign, setIsCampaign] = React.useState(false)
  const { data: campaigns } = useGetCampaignsQuery({
    unibox: true,
    option: true,
    workspaceId
  })
  const campaignsList = campaigns?.updatedEmail?.map((e) => e) || []
  const [sendDummyData] = useSendDummyDataMutation()
  const [addWebhook] = useAddWebhookMutation()

  const handleCloseAddWebhookDialog = () => {
    setOpenAddWebhookDialog(false)
    setIsCampaign(false)
    setEventType(null)
  }

  // Initial form values
  const initialValues = {
    webhookUrl: '',
    eventType: eventType || '',
    campaign: { id: '', name: '' }
  }

  const handleTestWebhook = async (values) => {
    try {
      const { message } = await sendDummyData({
        webhookUrl: values.webhookUrl,
        event: values.eventType,
        campaign: values.campaign
      }).unwrap()
      toast.success(message, { duration: 2000 })
    } catch (error) {
      // console.error(error)
      const errorMessage = error?.data?.error?.message || 'Test webhook failed'
      toast.error(errorMessage, { duration: 2000 })
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsCampaign(true)
    setIntegration(true)
    const toastId = toast.loading('Adding webhook...', { duration: Infinity })

    try {
      const { message } = await addWebhook({
        webhookUrl: values.webhookUrl,
        event: values.eventType,
        campaign: values.campaign,
        workspaceId
      }).unwrap()

      if (message.includes('Webhook with this event name and campaign already exists.')) {
        toast.error(message, { id: toastId, duration: 2000 })
      } else {
        toast.success(message, { id: toastId, duration: 2000 })
        setIsCampaign(false)
        setIntegration(false)
        handleCloseAddWebhookDialog()
      }
    } catch (error) {
      // console.error(error)
      const errorMessage = error?.data?.error?.message || 'An unexpected error occurred'
      toast.error(errorMessage, { id: toastId, duration: 2000 })
    } finally {
      setIsCampaign(false)
      setIntegration(false)
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={openAddWebhookDialog}
      onClose={handleCloseAddWebhookDialog}
      sx={{
        '& .MuiDialog-paper': {
          border: '1px solid #E4E4E5',
          borderRadius: '8px',
          width: '555px',
          minHeight: { xs: 'auto', md: '377px' }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: '16px', md: '16px 24px' }
      }}
      >
        <Typography sx={{ fontSize: { xs: '14px', md: '16px' }, fontWeight: 700, lineHeight: '20px' }}>Add Webhook</Typography>
        <IconButton
          onClick={handleCloseAddWebhookDialog}
          aria-label='close'
          sx={{
            border: '1px solid #E4E4E5',
            borderRadius: '6px',
            height: { xs: '32px', md: '35px' },
            width: { xs: '32px', md: '35px' }
          }}
        >
          <CloseIcon sx={{ fontSize: '15px', color: 'black' }} />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isValid, dirty, isSubmitting }) => (
          <Form>
            <DialogContent sx={{
              padding: { xs: '0px 16px', md: '16px 24px' },
              overflowY: 'hidden'
            }}
            >
              <Box sx={{ mb: '16px', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                  <Typography variant='body1' component='label' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    Webhook URL
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 500, ml: 1 }}>
                    Required
                  </Typography>
                </Box>
                <Field name='webhookUrl'>
                  {({ field }) => (
                    <TextField
                      {...field}
                      placeholder='https://...'
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          border: '1px solid #DCDEE0',
                          borderRadius: '8px',
                          height: '40px'
                        },
                        '& .MuiOutlinedInput-input': {
                          height: '40px',
                          padding: '0 14px',
                          boxSizing: 'border-box'
                        },
                        '& .MuiFormHelperText-root': {
                          position: 'absolute',
                          right: 0,
                          marginRight: 0,
                          marginTop: '36px'
                        }
                      }}
                      required
                      error={touched.webhookUrl && Boolean(errors.webhookUrl)}
                      helperText={touched.webhookUrl && errors.webhookUrl}
                    />
                  )}
                </Field>
              </Box>

              <Box sx={{ mb: '16px', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                  <Typography variant='body1' component='label' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    Event
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 500, ml: 1 }}>
                    Required
                  </Typography>
                </Box>
                <FormControl error={touched.eventType && Boolean(errors.eventType)} sx={{ width: '100%' }}>
                  <Field name='eventType'>
                    {({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        onChange={(e) => {
                          setFieldValue('eventType', e.target.value)
                          setEventType(e.target.value)
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>Select event in list</Typography>
                          }

                          // Find the selected option to display its icon and text
                          const selectedOption = eventOptions.find(option => option.value === selected)
                          if (selectedOption) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {selectedOption.icon}
                                <Typography sx={{ ml: 1, fontSize: '14px', fontWeight: 500 }}>{selectedOption.text}</Typography>
                              </Box>
                            )
                          }

                          return selected
                        }}
                        sx={{
                          width: '100%',
                          height: '40px',
                          '& .MuiOutlinedInput-root': {
                            border: '1px solid #DCDEE0',
                            borderRadius: '8px'
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              overflow: 'auto'
                            }
                          }
                        }}
                      >
                        <MenuItem disabled value=''>
                          <em>Select event in list</em>
                        </MenuItem>
                        {eventOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {option.icon}
                              <Typography sx={{ ml: 1, fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>{option.text}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Field>
                  {touched.eventType && errors.eventType && (
                    <FormHelperText>{errors.eventType}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                  <Typography variant='body1' component='label' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>
                    Campaign
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '12px', md: '13px' }, fontWeight: 500, ml: 1 }}>
                    Required
                  </Typography>
                </Box>
                <FormControl error={touched.campaign && Boolean(errors.campaign?.id || errors.campaign?.name)} sx={{ width: '100%' }}>
                  <Select
                    displayEmpty
                    value={values.campaign.name || ''}
                    onChange={(e) => {
                      const selectedOption = campaignsList.find(campaign => campaign.name === e.target.value)
                      if (selectedOption) {
                        setFieldValue('campaign', { id: selectedOption._id, name: selectedOption.name })
                      }
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Typography color='text.secondary' sx={{ fontSize: { xs: '12px', md: '14px' }, fontWeight: 500 }}>Select campaign in list</Typography>
                      }
                      return selected
                    }}
                    sx={{
                      width: '100%',
                      height: '40px',
                      fontSize: '14px',
                      fontWeight: 500,
                      '& .MuiOutlinedInput-root': {
                        border: '1px solid #DCDEE0',
                        borderRadius: '8px'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflow: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem disabled value=''>
                      <em>Select campaign in list</em>
                    </MenuItem>
                    {campaignsList.map((campaign) => (
                      <MenuItem key={campaign._id} value={campaign.name}>
                        <Typography
                          sx={{
                            fontSize: { xs: '12px', md: '14px' },
                            fontWeight: 500
                          }}
                        >
                          {campaign.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.campaign && (errors.campaign?.id || errors.campaign?.name) && (
                    <FormHelperText>{errors.campaign?.id || errors.campaign?.name}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </DialogContent>

            <DialogActions sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              padding: { xs: '16px', md: '16px 24px' }
            }}
            >
              <Button
                onClick={handleCloseAddWebhookDialog}
                sx={{
                  height: { xs: '34px', md: '42px' },
                  width: { xs: '100%', md: '76px' },
                  border: '1px solid #E4E4E5',
                  borderRadius: '8px',
                  fontSize: { xs: '12px', md: '14px' },
                  fontWeight: 600,
                  color: '#101010'
                }}
                type='button'
              >
                Cancel
              </Button>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: '8px', md: '0px' }, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
                <Button
                  disableRipple
                  disabled={!isValid || !dirty}
                  onClick={() => handleTestWebhook(values)}
                  sx={{
                    height: { xs: '34px', md: '42px' },
                    width: { xs: '100%', md: '130px' },
                    mr: '8px',
                    background: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 600
                  }}
                  type='button'
                >
                  Test Webhook
                </Button>
                <Button
                  variant='contained'
                  sx={{
                    height: { xs: '34px', md: '42px' },
                    width: { xs: '100%', md: '130px' },
                    borderRadius: '8px',
                    fontSize: { xs: '12px', md: '14px' },
                    fontWeight: 600,
                    color: '#fff'
                  }}
                  type='submit'
                  disabled={!isValid || !dirty || isSubmitting}
                >
                  Add Webhook
                </Button>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default AddWebhookDialog
